import React, { useState } from "react";
import styled from "styled-components";
import DataTable from "../components/DataTable";
import Popup from "../components/Popup";
import CreateRekvizForm from "./CreateRekvizForm";
import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import dayjs from "dayjs";
import apiClient from "../utils/apiClient";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import Provider from "../enum/Provider.enum";
import { useTranslation } from "react-i18next";

const ButtonsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const RekvizSectionContainer = styled.div`
  margin-top: 20px;
`;

const RekvizSection = ({ agentId }) => {
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { provider } = useSelector((state) => state.app);
  const isArmenia = provider === Provider.ARMENIA;

  const handleRefresh = () => {
    setRefresh((prev) => prev + 1);
  };

  const deleteRekviz = async (item) => {
    try {
      const confirmDelete = window.confirm(t("rekviz.confirmDelete"));
      if (!confirmDelete) return;

      const res = await apiClient.post("/rekviz_agents", { ...item, is_o: 0 });
      if (res.data.status === "ok") {
        alert(t("rekviz.deleted"));
        handleRefresh();
      } else {
        alert(res.data?.error ?? t("rekviz.deleteError"));
      }
    } catch (err) {
      console.error(t("rekviz.deleteErrorLog"), err);
      alert(err?.response?.data?.error ?? t("rekviz.deleteError"));
    }
  };

  return (
    <RekvizSectionContainer>
      <ButtonsBlock>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setEditingItem(null);
            setShowPopup(true);
          }}
          fullWidth
          startIcon={<AddIcon />}
        >
          {t("rekviz.add")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleRefresh}
          fullWidth
        >
          {t("common.refresh")}
        </Button>
      </ButtonsBlock>

      <DataTable
        path="/rekviz_agents"
        queryParams={{ agent_id: agentId }}
        refresh={refresh}
        columns={[
          {
            field: "date_create",
            headerName: t("rekviz.columns.dateCreate"),
            flex: 2,
            valueFormatter: (value) => {
              return value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "";
            },
          },
          {
            field: "is_default",
            headerName: t("rekviz.columns.isDefault"),
            flex: 1,
            type: "boolean",
            renderCell: (params) => {
              return params.value === true ? (
                <CheckIcon color="primary" />
              ) : null;
            },
          },
          { field: "r_s", headerName: t("rekviz.columns.account"), flex: 1 },
          { field: "bik", headerName: t("rekviz.columns.bik"), flex: 1 },
          { field: "curr", headerName: t("rekviz.columns.currency"), flex: 1 },
          {
            field: "recipient",
            headerName: t("rekviz.columns.recipient"),
            flex: 1,
          },
          {
            field: "description",
            headerName: t("rekviz.columns.description"),
            flex: 2,
          },
          { field: "bank_name", headerName: t("rekviz.columns.bank"), flex: 1 },
          {
            field: "action",
            headerName: "",
            width: 180,
            type: "actions",
            renderCell: (params) => (
              <ButtonsBlock>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setEditingItem(params.row);
                    setShowPopup(true);
                  }}
                >
                  {t("common.edit")}
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    deleteRekviz(params.row);
                  }}
                  disabled={params.row?.is_default}
                >
                  {t("common.delete")}
                </Button>
              </ButtonsBlock>
            ),
          },
        ]}
      />

      <Popup
        show={showPopup}
        title={editingItem ? t("rekviz.editTitle") : t("rekviz.add")}
        width="sm"
        onClose={() => setShowPopup(false)}
        ContentComponent={CreateRekvizForm}
        contentProps={{
          agentId,
          onClose: () => setShowPopup(false),
          onSuccess: () => {
            setShowPopup(false);
            handleRefresh();
          },
          item: editingItem,
        }}
        wrapContent={false}
      />
    </RekvizSectionContainer>
  );
};

export default RekvizSection;
